/**
 * What the desk's card takes from this page: the writes a card makes on the spot, the note composer,
 * the survey reading and the clip's small player (three other features' nodes), and the next-plan
 * dialog.
 *
 * The page is where these meet because a feature may not import a feature (`features/attention-queue`'s
 * `lib/taskWrites` says so at length). The writes are the three owning features' hooks, spread — the
 * names line up, so the adapter is nothing but the spread.
 *
 * ── A write that resolves the item strikes the card only once the server agreed ──────────────
 * `onResolved` is `resolveImplicitly` on the deck, called from the card's own success path
 * (`useTaskAct`): the same rule the survey, the clip and the note already follow here.
 */

import { useNavigate } from '@tanstack/react-router';
import { createElement, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { clipDestination, taskLabel, type AttentionItem, type TaskSlots } from '~/features/attention-queue';
import { useGoalWrites } from '~/features/client-goal';
import { ClipCardPlayer } from '~/features/clip-player';
import { CreatePlanDialog } from '~/features/create-plan';
import { useResendInvitation } from '~/features/invite-client';
import { usePlanLifecycle } from '~/features/plan-lifecycle';
import { SessionCommentComposer } from '~/features/session-comment';
import { SurveyTaskReading } from '~/features/survey-report';

export interface TaskSlotsState {
  readonly slots: TaskSlots;
  /** The next-plan dialog, mounted by the screen beside the desk. */
  readonly dialog: ReactNode;
  /** The clip card's reply field, which the card's „Zostaw notatkę" focuses. One card is on the desk. */
  readonly clipField: RefObject<HTMLInputElement | null>;
}

export function useTaskSlots({
  announce,
  onResolved,
  onNoteSaved,
  reducedMotion,
}: {
  readonly announce: (message: string) => void;
  /** The server agreed: strike the card. */
  readonly onResolved: (item: AttentionItem) => void;
  /** The note saved — the screen's own implicit close, which also announces it. */
  readonly onNoteSaved: (item: AttentionItem) => void;
  readonly reducedMotion: boolean;
}): TaskSlotsState {
  const { t } = useTranslation();
  const plan = usePlanLifecycle();
  const goal = useGoalWrites();
  const invitation = useResendInvitation();
  const [nextPlanFor, setNextPlanFor] = useState<AttentionItem | null>(null);
  const clipField = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  return {
    slots: {
      writes: { ...plan, ...goal, ...invitation },
      note: (item, drafts) =>
        item.action.kind === 'sessionNote'
          ? createElement(SessionCommentComposer, {
              workoutExecutionId: item.action.workoutExecutionId,
              savedComment: '',
              drafts,
              compact: true,
              onSaved: () => {
                onNoteSaved(item);
              },
            })
          : null,
      survey: (survey, density) => createElement(SurveyTaskReading, { ...survey, density, reducedMotion }),
      clipPlayer: (clip) =>
        createElement(ClipCardPlayer, {
          ...clip,
          fieldRef: clipField,
          reducedMotion,
          // „Analizuj": the full player on the client's card, which Escape leaves back to this desk.
          onOpen: () => {
            void navigate(clipDestination({ clientId: clip.clientId, clipId: clip.clipId }));
          },
        }),
      assignNext: (item) => {
        setNextPlanFor(item);
      },
      onResolved,
      announce,
    },
    clipField,
    dialog:
      nextPlanFor === null || nextPlanFor.subjectCustomerId === null
        ? null
        : createElement(CreatePlanDialog, {
            recipient: { clientId: nextPlanFor.subjectCustomerId, clientName: taskLabel(nextPlanFor, t) },
            open: true,
            // The card that opened it may have been struck and left the desk by the time this
            // closes; the desk's own heading is what stays.
            finalFocus: () => document.querySelector<HTMLElement>('main h1'),
            onClose: () => {
              setNextPlanFor(null);
            },
          }),
  };
}
